import { Link, useRouter } from "@shared/client/navigation";
import { useState } from "react";
import { Check, LoaderCircle, Send, Undo2, X } from "lucide-react";
import {
  effectiveClass,
  MESSAGE_STATUS_LABELS,
  RANK_LABELS,
  REPLY_CLASS_LABELS,
  REPLY_CLASSES,
  type ReplyClass,
  type ReplyInboxItem,
} from "@shared/lib/prospection/shared";
import { formatDateTime } from "@shared/lib/format";
import { AdminChip, AdminEmpty, AdminMeta, AdminPanel } from "@shared/components/blocks";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Button } from "@shared/components/ui/button";
import { Label } from "@shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/components/ui/select";
import { Textarea } from "@shared/components/ui/textarea";
import { cn } from "@shared/lib/utils";

/**
 * Les réponses des prospects, toutes boîtes confondues. « Oui » et questions
 * en tête, avec la réponse préparée par la machine, à relire puis envoyer dans
 * le même fil. Un reclassement corrige le modèle et déclenche ce que la
 * nouvelle classe prévoit.
 */

const CLASS_TONES: Record<ReplyClass, string> = {
  yes: "active",
  question: "info",
  later: "trial",
  not_interested: "neutral",
  unsubscribe: "churned",
  auto_reply: "neutral",
  bounce: "churned",
  unclear: "warning",
};

const PRIORITY: ReplyClass[] = ["yes", "question", "unclear", "later", "not_interested", "auto_reply", "unsubscribe", "bounce"];

async function api(path: string, body: Record<string, unknown>) {
  const response = await fetch(path, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error((data as { error?: string }).error || "Opération impossible.");
}

function waiting(since: string, now: number) {
  const minutes = Math.max(0, Math.round((now - new Date(since).getTime()) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  return hours < 48 ? `${hours} h` : `${Math.round(hours / 24)} j`;
}

export function RepliesView({ items, filter }: { items: ReplyInboxItem[]; filter: "pending" | "handled" }) {
  const [now] = useState(() => Date.now());
  const sorted = [...items].sort(
    (a, b) => PRIORITY.indexOf(effectiveClass(a)) - PRIORITY.indexOf(effectiveClass(b)) || b.received_at.localeCompare(a.received_at),
  );

  return (
    <>
      <AdminMeta note="La machine ne répond seule qu’aux « oui » nets, si le réglage l’autorise.">
        <span className="flex gap-4">
          <Link className={cn("hover:text-foreground", filter === "pending" && "font-medium text-foreground")} href="/admin/prospection/reponses">
            À traiter
          </Link>
          <Link
            className={cn("hover:text-foreground", filter === "handled" && "font-medium text-foreground")}
            href="/admin/prospection/reponses?filtre=traitees"
          >
            Traitées
          </Link>
        </span>
      </AdminMeta>

      {sorted.length === 0 ? (
        <AdminPanel>
          <AdminEmpty>{filter === "pending" ? "Aucune réponse en attente." : "Aucune réponse traitée."}</AdminEmpty>
        </AdminPanel>
      ) : (
        <ul className="m-0 grid list-none gap-4 p-0">
          {sorted.map((item) => (
            <ReplyCard item={item} key={`${item.id}-${item.corrected_at ?? ""}-${item.answer?.updated_at ?? ""}`} now={now} />
          ))}
        </ul>
      )}
    </>
  );
}

function ReplyCard({ item, now }: { item: ReplyInboxItem; now: number }) {
  const router = useRouter();
  const [body, setBody] = useState(item.answer?.body ?? "");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const current = effectiveClass(item);
  const answer = item.answer;

  async function act(key: string, path: string, payload: Record<string, unknown>) {
    setBusy(key);
    setError("");
    try {
      await api(path, payload);
      router.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Opération impossible.");
    } finally {
      setBusy("");
    }
  }

  return (
    <li>
      <AdminPanel
        action={
          <span className="text-[12px] text-muted-foreground">
            reçue le {formatDateTime(item.received_at)}
            {!item.handled_at ? ` · attend depuis ${waiting(item.received_at, now)}` : ""}
          </span>
        }
        title={item.prospect ? `${item.prospect.name}${item.prospect.contact_name ? ` — ${item.prospect.contact_name}` : ""}` : item.from_email ?? "Expéditeur inconnu"}
      >
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="grid content-start gap-3">
            <div className="flex flex-wrap items-center gap-3 text-[12.5px]">
              <AdminChip tone={CLASS_TONES[current]}>{REPLY_CLASS_LABELS[current]}</AdminChip>
              <span className="text-muted-foreground">
                {item.corrected_class
                  ? "reclassée à la main"
                  : item.class_source === "rule"
                    ? "classée par les règles"
                    : item.class_source === "model"
                      ? `classée par le modèle${item.confidence !== null ? ` (${Math.round(item.confidence * 100)} %)` : ""}`
                      : "modèle indisponible"}
                {item.after_rank ? ` · après ${RANK_LABELS[item.after_rank]?.toLowerCase()}` : ""}
              </span>
            </div>
            <p className="m-0 text-[12.5px] text-muted-foreground">
              {item.from_email}
              {item.subject ? ` · ${item.subject}` : ""}
              {item.return_on ? ` · retour annoncé le ${item.return_on}` : ""}
            </p>
            <pre className="m-0 rounded-lg border bg-muted/30 p-4 font-sans text-[13px] leading-relaxed whitespace-pre-wrap">
              {item.excerpt || "(message vide)"}
            </pre>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                onValueChange={(value) => act("class", `/api/admin/prospection/replies/${item.id}`, { class: value })}
                value={current}
              >
                <SelectTrigger aria-label="Reclasser la réponse" className="h-8 text-xs font-semibold" size="sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPLY_CLASSES.map((value) => (
                    <SelectItem key={value} value={value}>{REPLY_CLASS_LABELS[value]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {item.handled_at ? (
                <Button
                  disabled={busy === "handled"}
                  onClick={() => act("handled", `/api/admin/prospection/replies/${item.id}`, { handled: false })}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  <Undo2 /> Rouvrir
                </Button>
              ) : (
                <Button
                  disabled={busy === "handled"}
                  onClick={() => act("handled", `/api/admin/prospection/replies/${item.id}`, { handled: true })}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  <Check /> Marquer traitée
                </Button>
              )}
              {item.prospect && (
                <Link className="text-[12.5px] text-muted-foreground hover:text-foreground" href={`/admin/prospection/${item.prospect.id}`}>
                  Ouvrir la fiche
                </Link>
              )}
            </div>
          </div>

          <div className="grid content-start gap-3">
            {!answer ? (
              <p className="m-0 text-[12.5px] leading-relaxed text-muted-foreground">
                {current === "unclear"
                  ? "Aucune réponse préparée : reclassez le message, ou répondez depuis Gmail."
                  : "Rien à répondre pour cette classe : la machine a fait ce qu’elle prévoit (séquence arrêtée, adresse exclue ou relance décalée)."}
              </p>
            ) : answer.status === "to_review" || answer.status === "held" ? (
              <>
                <Label htmlFor={`answer-${item.id}`}>Réponse préparée, envoyée dans le même fil</Label>
                <Textarea
                  className="text-[13px] leading-relaxed"
                  id={`answer-${item.id}`}
                  maxLength={8000}
                  onChange={(event) => setBody(event.target.value)}
                  rows={10}
                  value={body}
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    disabled={busy === "send" || !body.trim()}
                    onClick={() => act("send", `/api/admin/prospection/messages/${answer.id}`, { action: "approve", body })}
                    size="sm"
                    type="button"
                  >
                    {busy === "send" ? <LoaderCircle className="animate-spin" /> : <Send />} Envoyer
                  </Button>
                  <Button
                    className="text-muted-foreground hover:text-destructive"
                    disabled={busy === "cancel"}
                    onClick={() => act("cancel", `/api/admin/prospection/messages/${answer.id}`, { action: "cancel" })}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    <X /> Ne pas répondre
                  </Button>
                </div>
              </>
            ) : (
              <>
                <span className="text-[12px] font-medium text-muted-foreground">
                  Réponse : {MESSAGE_STATUS_LABELS[answer.status]}
                  {answer.approved_auto ? " (automatique)" : ""}
                  {answer.sent_at ? ` le ${formatDateTime(answer.sent_at)}` : ""}
                </span>
                <pre className="m-0 rounded-lg border p-4 font-sans text-[13px] leading-relaxed whitespace-pre-wrap">{answer.body}</pre>
                {answer.error && <p className="m-0 text-[12.5px] text-destructive">{answer.error}</p>}
              </>
            )}
          </div>
        </div>

        {error && (
          <Alert className="mt-4" role="alert" variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </AdminPanel>
    </li>
  );
}
